import React from 'react';
import { NavigationProp, useNavigation } from '@react-navigation/native';
import { faFileUpload } from '@fortawesome/free-solid-svg-icons';
import { FacialParamList } from '../../../typings/Params';
import Filters from '../../../components/Filter';
import FilterInput from '../../../components/Filter/components/FilterInput';
import { translate } from '../../../services/translate';
import { useState } from 'react';
import FilterCheckBox from '../../../components/Filter/components/FilterCheckBox';
import MarkerDropdown from '../../../components/facial/MarkerDropdown';
import PersonTypeDropdown from '../../../components/facial/PersonTypeDropdown';
import { PersonType } from '../../../services/central-api/facial';
import FileUploader from '../../../components/FileUploader';

interface Props {
    filters: FacialParamList['PeopleList'];
    setFilters: React.Dispatch<React.SetStateAction<FacialParamList['PeopleList'] & { photo?: File; }>>;
    textFilter: string;
    setTextFilter: React.Dispatch<React.SetStateAction<string>>;
    file: File | undefined;
    setFile: React.Dispatch<React.SetStateAction<File | undefined>>;
    previewUrl?: string;
}

export default function PeopleFilter({ filters, setFilters, textFilter, setTextFilter, file, setFile, previewUrl }: Props) {
    const navigation = useNavigation<NavigationProp<FacialParamList, 'PeopleList'>>();

    const [onlyActive, setOnlyActive] = useState<boolean>(filters.onlyActive ?? true);
    const [selectedMarkersIds, setSelectedMarkersIds] = useState<number[]>(filters.markers ?? []);
    const [personType, setPersonType] = useState<PersonType | ''>(filters.personType ?? '');

    const hasImage = !!file;

    const handleSelectFile = (selected: File) => {
        setTextFilter('');
        setOnlyActive(true);
        setSelectedMarkersIds([]);
        setPersonType('');
        setFile(selected);
    };

    const handleCleanFilter = () => {
        setTextFilter('');
        setOnlyActive(true);
        setSelectedMarkersIds([]);
        setPersonType('');
        setFile(undefined);
    };

    const handleFilter = () => {
        if (hasImage) {
            setFilters({
                ...filters,
                nameOrDocument: undefined,
                photo: file,
                page: 0,
                onlyActive: false,
            });
            setOnlyActive(false);
            navigation.setParams({
                nameOrDocument: undefined,
                page: 1,
                onlyActive: false,
                markers: undefined,
                personType: undefined,
            });
            return;
        }

        setFilters({
            ...filters,
            photo: undefined,
            nameOrDocument: textFilter ? textFilter : undefined,
            page: 0,
            onlyActive: onlyActive ? onlyActive : undefined,
            markers: selectedMarkersIds.length ? selectedMarkersIds : undefined,
            personType: personType !== '' ? personType : undefined,
        });

        navigation.setParams({
            nameOrDocument: textFilter ? textFilter : undefined,
            page: 1,
            onlyActive: onlyActive ? onlyActive : undefined,
            markers: selectedMarkersIds.length ? selectedMarkersIds : undefined,
            personType: personType !== '' ? personType : undefined,
        });
    };

    return (
        <Filters onCleanFilters={handleCleanFilter} onFilters={handleFilter}>
            <FilterInput
                label={translate('searchNameDocument')}
                placeholder={translate('typeToSearch')}
                value={textFilter}
                disabled={hasImage}
                onChange={setTextFilter}
                showTooltip={hasImage}
                tooltipText={translate('filtersDisabledByImage')}
            />
            <FilterCheckBox
                label={translate('onlyActive')}
                value={hasImage ? false : onlyActive}
                onChange={setOnlyActive}
                disabled={hasImage}
                showTooltip={hasImage}
                tooltipText={translate('filtersDisabledByImage')}
                zIndex={4}
            />
            <PersonTypeDropdown
                setValue={setPersonType}
                value={personType}
                zIndex={3}
                includeEmptyItem={true}
                type='write'
                disabled={hasImage}
                showTooltip={hasImage}
                tooltipText={translate('filtersDisabledByImage')}
            />
            <MarkerDropdown
                setValue={setSelectedMarkersIds}
                value={selectedMarkersIds}
                zIndex={2}
                disabled={hasImage}
                showTooltip={hasImage}
                tooltipText={translate('filtersDisabledByImage')}
            />
            <FileUploader
                accept='image/*'
                text={translate('searchByPicture')}
                icon={faFileUpload}
                type='secondary'
                previewUrl={previewUrl}
                onRemove={() => setFile(undefined)}
                onChange={(e) => {
                    if (!e.target.files) {
                        return;
                    }
                    handleSelectFile(e.target.files[0]);
                }}
            />
        </Filters>
    );
}
